import type { MaterialSpec, SceneNode } from "@uzverse/world-scene";
import { useToast } from "@uzverse/ui";
import { useEffect, useRef, useState } from "react";
import { useErrorText } from "../components/common.js";
import { invokeWorldTool } from "./data.js";
import { useDockT } from "./dock-i18n.js";
import { getWorldFocus, useWorldState, worldStore } from "./store.js";
import type { WorldStore } from "./store.js";

/** Six presets and three paints. `swatch` is only for the button; the world renders the spec. */
export const QUICK_MATERIALS = [
  { key: "plaster", swatch: "#d9d2c3", spec: { preset: "plaster" } },
  { key: "brick", swatch: "#a4503a", spec: { preset: "brick" } },
  { key: "glass", swatch: "#9ec5e0", spec: { preset: "glass" } },
  { key: "wood", swatch: "#8b6b45", spec: { preset: "wood" } },
  { key: "concrete", swatch: "#9a9a98", spec: { preset: "concrete" } },
  { key: "red", swatch: "#b22222", spec: { color: "#b22222" } },
  { key: "blue", swatch: "#2f5fb3", spec: { color: "#2f5fb3" } },
  { key: "green", swatch: "#3a8a4f", spec: { color: "#3a8a4f" } },
] as const satisfies readonly { key: string; swatch: string; spec: MaterialSpec }[];

const SIDES = ["n", "e", "s", "w"] as const;
/** How long the "Undo" chip stays after a change. */
const UNDO_MS = 20_000;

/** Floor levels of a building node (`floors` is a count or a list of `{ level }`). */
function levelsOf(node: SceneNode): number[] {
  const floors = (node.geometry as { params?: { floors?: unknown } }).params?.floors;
  if (typeof floors === "number") return Array.from({ length: Math.max(0, floors) }, (_, i) => i);
  if (Array.isArray(floors)) {
    return floors
      .map((f) => (f as { level?: unknown }).level)
      .filter((l): l is number => typeof l === "number" && l >= 0);
  }
  return [];
}

/** The parts a quick material applies to: the picked part, else every wall of the building. */
export function partsFor(node: SceneNode, part: string | undefined): string[] {
  if (part) return [part];
  if (node.entityType === "world.placement") {
    return levelsOf(node).flatMap((level) => SIDES.map((s) => `f${level}.wall.${s}`));
  }
  return ["body"];
}

/**
 * Eight one-click materials for what the user selected. They call `world.setMaterial` through
 * the human invoke route (no model in between), flash the part at once, and leave an "Undo"
 * chip that runs `history.undo` with the correlation id the call returned.
 */
export function MaterialPicker({ store = worldStore }: { store?: WorldStore }) {
  const t = useDockT();
  const toast = useToast();
  const errorText = useErrorText();
  const { selected } = useWorldState(store);
  const node = selected ? store.node(selected.id) : undefined;
  const [busy, setBusy] = useState(false);
  const [undoable, setUndoable] = useState<{ correlationId: string; label: string } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  function offerUndo(correlationId: string, label: string) {
    if (timer.current) clearTimeout(timer.current);
    setUndoable({ correlationId, label });
    timer.current = setTimeout(() => setUndoable(null), UNDO_MS);
  }

  async function apply(key: (typeof QUICK_MATERIALS)[number]["key"]) {
    if (!selected || !node || busy) return;
    const preset = QUICK_MATERIALS.find((m) => m.key === key)!;
    const parts = partsFor(node, selected.part);
    if (parts.length === 0) return;
    setBusy(true);
    store.flash(node.id, parts); // optimistic: the delta repaints it a moment later
    try {
      const result = await invokeWorldTool(
        "world.setMaterial",
        { targetId: node.id, parts, material: preset.spec },
        getWorldFocus(store),
      );
      if (result.correlationId) offerUndo(result.correlationId, t(`material.${key}`));
    } catch (err) {
      toast.show(errorText(err), "error");
    } finally {
      setBusy(false);
    }
  }

  async function undo() {
    if (!undoable || busy) return;
    const { correlationId } = undoable;
    setBusy(true);
    try {
      await invokeWorldTool("history.undo", { correlationId });
      setUndoable(null);
      toast.show(t("undoDone"));
    } catch (err) {
      toast.show(errorText(err), "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="dock-picker" role="group" aria-label={t("pickerLabel")}>
      {node ? (
        <>
          {QUICK_MATERIALS.map((m) => (
            <button
              key={m.key}
              type="button"
              className="dock-swatch"
              style={{ background: m.swatch }}
              disabled={busy}
              aria-label={t(`material.${m.key}`)}
              title={t(`material.${m.key}`)}
              data-material={m.key}
              onClick={() => void apply(m.key)}
            />
          ))}
          {undoable ? (
            <button
              type="button"
              className="btn btn-sm dock-undo"
              disabled={busy}
              onClick={() => void undo()}
              title={t("applied", { material: undoable.label })}
            >
              {t("undo")}
            </button>
          ) : null}
        </>
      ) : (
        <span className="muted small">{t("pickerNone")}</span>
      )}
    </div>
  );
}
